import React, { useMemo, useState } from 'react'

import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { Box, Collapse, Typography } from '@material-ui/core'

import { Permitted } from 'components'
import {
  BulkActionButton,
  BulkActionDialog,
  BulkActionResultDialog,
} from './components'

import useSnackbar from 'hooks/useSnackbar'

import useStyles from './styles'

const defaultItemId = (item) => item?.id

const BulkActions = ({
  items = [],
  selected = [],
  setSelected,
  actions = [],
  describeItem,
  itemLabel = { singular: 'item', plural: 'itens' },
  refresh,
}) => {
  const [action, setAction] = useState(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const selectedItems = useMemo(() => {
    const selectedIds = new Set(selected)

    return items.filter((item) => selectedIds.has(defaultItemId(item)))
  }, [items, selected])

  const isEnabled = (currentAction) =>
    currentAction?.isEnabled
      ? currentAction.isEnabled(selectedItems)
      : !isEmpty(selectedItems)

  const buildDescription = (currentAction, itemId, detail) => {
    const getId = currentAction?.itemId || defaultItemId
    const item = selectedItems.find(
      (selectedItem) => getId(selectedItem) === itemId,
    )
    const description = item && describeItem ? describeItem(item) : {}

    return {
      id: itemId,
      name: description?.name || `${itemLabel.singular} ${itemId}`,
      subtitle: description?.subtitle,
      detail,
    }
  }

  const buildResult = (currentAction, data) => {
    const succeeded = data?.succeeded || []
    const skipped = data?.skipped || []
    const failed = data?.failed || []

    return {
      title: `${currentAction.title} — Resultado`,
      succeededCount: succeeded.length,
      skipped: skipped.map((item) =>
        buildDescription(currentAction, item?.id, item?.message),
      ),
      failed: failed.map((item) =>
        buildDescription(currentAction, item?.id, item?.error),
      ),
    }
  }

  const handleConfirm = async (values) => {
    setLoading(true)

    try {
      const getId = action.itemId || defaultItemId
      const response = await action.request({
        ids: selectedItems.map(getId),
        items: selectedItems,
        values,
      })

      const actionResult = buildResult(action, response?.data)

      if (!isEmpty(actionResult.failed) || !isEmpty(actionResult.skipped)) {
        setResult(actionResult)
      } else {
        snackbar.open({
          message: action.successMessage,
          variant: 'success',
        })
      }

      setSelected([])
      setAction(null)
      refresh()
    } catch (error) {
      snackbar.open({
        message: error?.response?.data?.error?.message || action.errorMessage,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Collapse in={!isEmpty(selected)} unmountOnExit>
        <Box className={classes.toolbar}>
          <Typography variant="body2" className={classes.counter}>
            {selected.length} selecionado(s)
          </Typography>
          {actions.map((currentAction) => (
            <Permitted
              key={currentAction.key}
              tag={currentAction.permission}
              someTags={currentAction.somePermissions}
            >
              <BulkActionButton
                label={currentAction.label}
                icon={currentAction.icon}
                hint={currentAction.hint}
                disabled={!isEnabled(currentAction)}
                disabledHint={currentAction.disabledHint}
                className={currentAction.className}
                onClick={() => setAction(currentAction)}
              />
            </Permitted>
          ))}
        </Box>
      </Collapse>

      {action && (
        <BulkActionDialog
          open={!isEmpty(action)}
          setOpen={() => setAction(null)}
          title={action.title}
          confirmText={action.confirmText}
          description={`${selected.length} ${itemLabel.plural} selecionado(s) será(ão) afetado(s).`}
          warning={action.warning}
          renderFields={action.renderFields}
          validateFields={action.validateFields}
          loading={loading}
          onConfirm={handleConfirm}
        />
      )}

      {result && (
        <BulkActionResultDialog
          open={!isEmpty(result)}
          setOpen={() => setResult(null)}
          title={result.title}
          succeededCount={result.succeededCount}
          skipped={result.skipped}
          failed={result.failed}
        />
      )}
    </>
  )
}

BulkActions.propTypes = {
  items: PropTypes.array,
  selected: PropTypes.array,
  setSelected: PropTypes.func.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      hint: PropTypes.string,
      disabledHint: PropTypes.string,
      className: PropTypes.string,
      permission: PropTypes.string,
      somePermissions: PropTypes.array,
      isEnabled: PropTypes.func,
      itemId: PropTypes.func,
      title: PropTypes.string.isRequired,
      confirmText: PropTypes.string,
      warning: PropTypes.node,
      renderFields: PropTypes.func,
      validateFields: PropTypes.func,
      request: PropTypes.func.isRequired,
      successMessage: PropTypes.string,
      errorMessage: PropTypes.string,
    }),
  ),
  describeItem: PropTypes.func,
  itemLabel: PropTypes.shape({
    singular: PropTypes.string,
    plural: PropTypes.string,
  }),
  refresh: PropTypes.func,
}

BulkActions.defaultProps = {
  refresh: () => {},
}

export default BulkActions
